import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, ArrowUp, ArrowDown, ChevronsUp, ListOrdered } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { adminNewsletterQueue, adminNewsletterQueueSave } from '../services/api';

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('hu-HU'); } catch { return ''; }
};

export const QueueEditorDialog = ({ adminKey, open, onClose, onSaved }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDirty(false);
    setLoading(true);
    adminNewsletterQueue(adminKey)
      .then((d) => setItems(d.queue || []))
      .catch(() => toast.error('A várólista betöltése nem sikerült.'))
      .finally(() => setLoading(false));
  }, [open, adminKey]);

  const move = (index, delta) => {
    setItems((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setDirty(true);
  };

  const moveTop = (index) => {
    setItems((prev) => {
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.unshift(item);
      return next;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminNewsletterQueueSave(adminKey, items.map((i) => i.slug));
      toast.success('Sorrend elmentve.');
      setDirty(false);
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A mentés nem sikerült.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="queue-editor-dialog" className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListOrdered size={18} className="text-sky-600" />
            Cikk-sorozat sorrendje
          </DialogTitle>
          <DialogDescription>
            Minden feliratkozó ebben a sorrendben kapja a következő, még nem küldött cikket.
            Az újonnan publikált cikkek automatikusan a lista végére kerülnek.
            A borostyán jelölésű cikkeket a fix drip sorozat (D0–D35) is érinti — ezek alapból
            a sor végén állnak, de bárhova átrendezheted őket.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-2 py-1">
          {loading ? (
            <div className="text-center py-10"><Loader2 className="animate-spin mx-auto text-sky-500" size={24} /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 text-gray-500 text-sm">
              Nincs cikk a sorban — publikálj újat a Blog editorban!
            </div>
          ) : (
            items.map((item, i) => (
              <div
                key={item.slug}
                data-testid={`queue-item-${item.slug}`}
                className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5"
              >
                <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-sky-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate flex items-center gap-2">
                    <span className="truncate">{item.title}</span>
                    {item.drip_covered && (
                      <span data-testid={`queue-covered-${item.slug}`} className="shrink-0 text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-300 rounded px-1.5 py-0.5">
                        fix drip is küldi
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {item.category} · {fmtDate(item.published_at)}
                    {item.received_by > 0 && ` · ${item.received_by} feliratkozó már megkapta`}
                    {i === 0 && <span className="text-sky-600 font-semibold"> · KÖVETKEZŐ</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {i > 0 && (
                    <button
                      type="button"
                      data-testid={`queue-top-${item.slug}`}
                      title="Legyen a következő"
                      onClick={() => moveTop(i)}
                      className="p-1.5 rounded-md text-sky-600 hover:bg-sky-100 transition-colors"
                    >
                      <ChevronsUp size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid={`queue-up-${item.slug}`}
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    className="p-1.5 rounded-md text-gray-600 hover:bg-gray-200 disabled:opacity-30 transition-colors"
                  >
                    <ArrowUp size={16} />
                  </button>
                  <button
                    type="button"
                    data-testid={`queue-down-${item.slug}`}
                    onClick={() => move(i, 1)}
                    disabled={i === items.length - 1}
                    className="p-1.5 rounded-md text-gray-600 hover:bg-gray-200 disabled:opacity-30 transition-colors"
                  >
                    <ArrowDown size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <Button data-testid="queue-editor-cancel" variant="outline" onClick={onClose}>
            Mégse
          </Button>
          <Button
            data-testid="queue-editor-save"
            onClick={handleSave}
            disabled={saving || !dirty}
            className="bg-sky-600 hover:bg-sky-500 text-white font-bold"
          >
            {saving ? <><Loader2 className="mr-1.5 animate-spin" size={14} /> Mentés…</> : <><Save size={14} className="mr-1.5" /> Sorrend mentése</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QueueEditorDialog;
